import { useEffect, useRef, useState } from "react";
import { ChatMessagesWrapper } from "./style";
import { APIError, downloadFileError } from "../../../core/utils";
import profilePic from "../../../assets/Images/icons/profile-pic.png";
import API from "../../../api/axios";
import { APP_URLS } from "../../../api/url";
import { toast } from "react-toastify";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import NoRecord from "../../../assets/SharedComponents/Table/NoRecord";
import back from "../../../assets/Images/icons/svgIcon/back-arrow.svg";
import { ThreeDots } from "react-loader-spinner";
import moment from "moment";
import { Button } from "@mui/material";
import download from "downloadjs";
import {
  EmailValidationRegex,
  linkRegex,
  PhoneValidationRegex,
} from "../../../core/regex";
import sanitizeHtml from "sanitize-html";
import linkifyHtml from "linkify-html";

const options = {
  defaultProtocol: "https",
  attributes: (type) => {
    if (type === "url") {
      // For URLs, open in a new tab
      return {
        target: "_blank",
        rel: "noopener noreferrer",
      };
    }
    // For emails or other types, no special attributes
    return {};
  },
};

const getTime = (time) => {
  if (!moment.isMoment(time)) {
    time = moment(time);
  }
  if (time.isSame(moment(), "day")) {
    // if chat of same day then display today
    return "Today";
  } else if (time.isSame(moment().subtract(1, "d"), "day")) {
    // if chat from last day (yesterday)
    return "Yesterday";
  } else if (time.isSame(moment().subtract(7, "days"), "week")) {
    // if chat last week then display (week day name)
    return time.format("dddd");
  } else {
    return time.format("DD/MM/YYYY"); // if chat is 1 week or more days ago then display date
  }
};

const ChatMesages = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { chatID } = useParams();

  const scrollRef = useRef(null);
  const scrollTimeoutRef = useRef(null); // 👈 For debounce

  const [page, setPage] = useState(1);
  const [messages, setMessages] = useState([]);
  const [chatsLoader, setChatsLoader] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  // Fetch messages when `page` changes
  useEffect(() => {
    if (!hasMore) return;
    getMessages(page);
  }, [page]);

  // Scroll handler with debounce
  const handleScroll = (e) => {
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }

    scrollTimeoutRef.current = setTimeout(() => {
      const bottom =
        e.target.scrollHeight + e.target.scrollTop - 10 <=
        e.target.clientHeight;

      if (bottom && hasMore && !chatsLoader) {
        setPage((prevPage) => prevPage + 1);
      }
    }, 200); // 200ms debounce
  };

  const getMessages = (pageNumber) => {
    setChatsLoader(true);
    API.get(`${APP_URLS.GET_MESSAGES}/${chatID}?page=${pageNumber}`)
      .then((res) => {
        const resp = res.data;
        if (resp.success === true) {
          const newMessages = resp.data.data;
          setMessages((prev) => [...prev, ...newMessages]);
          if (resp.data.last_page > 1 && resp.data.current_page == 1) {
            setPage((prevPage) => prevPage + 1);
          }
          if (resp.data.current_page === resp.data.last_page) {
            setHasMore(false);
          }
        } else {
          toast.error(resp.message, {
            position: toast.POSITION.TOP_RIGHT,
          });
        }
      })
      .catch((error) => {
        toast.error(APIError(error), {
          position: toast.POSITION.TOP_RIGHT,
        });
      })
      .finally(() => {
        setChatsLoader(false);
      });
  };

  const linkGenerator = (matched) => {
    let withProtocol = matched;
    if (!withProtocol.startsWith("http")) {
      withProtocol = "http://" + matched;
    }
    let checkEmail = EmailValidationRegex.email.test(matched);
    let checkPhone = PhoneValidationRegex.phone.test(matched);

    if (checkEmail) {
      return `<a className="text-link" href="mailto:${matched}">${matched}</a>`;
    }
    if (checkPhone) {
      return `<a className="text-link" href="tel:${matched}">${matched}</a>`;
    }
    return `<a className="text-link" href="${withProtocol}" target="_blank" rel="noopener noreferrer">${matched}</a>`;
  };

  const getMessageHtml = (message) => {
    // Manual linking via full_title & url
    if (message.url && message.full_title) {
      const replaced = message.message.replace(
        message.full_title,
        `<a className="text-link" href="${message.url}" target="_blank" rel="noopener noreferrer">${message.full_title}</a>`
      );
      return sanitizeHtml(replaced.replace(/(?:\r\n|\r|\n)/g, "<br>"), {
        allowedTags: sanitizeHtml.defaults.allowedTags.concat(["br", "a"]),
        allowedAttributes: {
          a: ["href", "target", "rel", "class"],
        },
      });
    }

    // Detect & replace links or phones
    let formatted = message;
    if (linkRegex.test(message)) {
      formatted = formatted.replace(linkRegex, linkGenerator);
    } else if (PhoneValidationRegex.phone.test(message)) {
      formatted = formatted.replace(PhoneValidationRegex.phone, linkGenerator);
    }

    // Replace newlines with <br> tags
    const withBreaks = formatted.replace(/(?:\r\n|\r|\n)/g, "<br>");

    // Final sanitize
    return sanitizeHtml(withBreaks, {
      allowedTags: sanitizeHtml.defaults.allowedTags.concat(["br", "a"]),
      allowedAttributes: {
        a: ["href", "target", "rel", "class"],
      },
    });
  };

  const downloadPdf = (media) => {
    API.get(`/chats/${media.id}/download`, { responseType: "blob" })
      .then((res) => {
        const resp = res.data;
        if (resp !== null) {
          toast.success("Downloading. Please wait", {
            position: toast.POSITION.TOP_RIGHT,
          });
          const content = res.headers["content-type"];
          download(resp, media.file_name, content);
        } else {
          toast.error(resp.message, {
            position: toast.POSITION.TOP_RIGHT,
          });
        }
      })
      .catch(async function (error) {
        let error_message = await downloadFileError(error);
        toast.error(error_message, {
          position: toast.POSITION.TOP_RIGHT,
        });
      });
  };

  const handleNavigate = () => {
    if (location.search.includes('from')) {
      navigate(-1);
      return; // Prevents fallthrough
    }
    navigate(`/reported-chats${location.search}`);
  };

  return (
    <ChatMessagesWrapper>
      <div className="auctionMainContainer">
        <div className="row mt-4">
          <div className="col-md-4 d-flex align-items-center">
            <img src={back} className="ms-4 backbtn" onClick={handleNavigate} />
          </div>
          <div className="col-md-4 text-align-center">
            <h1>Messages</h1>
          </div>
        </div>
        {messages.length > 0 ? (
          <div
            className="messagesContainer"
            onScroll={handleScroll}
            ref={scrollRef}
          >
            {messages.map((message, index) => {
              let date = new Date(message.created_at).toDateString();
              let nextDate = "";
              if (messages.length > 1) {
                nextDate = new Date(
                  messages[index + 1]?.created_at
                ).toDateString();
              }
              return (
                <>
                  <div className="messageBox" key={`${message.id}-${index}`}>
                    <img
                      src={message.chat_user_info.profile_picture_url ?? profilePic}
                      className="senderImage"
                      alt="Sender"
                    />
                    <div className="messageAndSenderInfo">
                      <span className="sender">
                        {message.chat_user_info.name},{" "}
                        {moment(message.created_at).format("HH:mm")}
                      </span>
                      {message?.media?.length > 0 &&
                        message.media.map((media, i) => (
                          <div className="mediaBox" key={i}>
                            {media.mime_type.includes("image") && (
                              <img
                                src={media.medium_url}
                                className="messageImage"
                                alt="Media"
                              />
                            )}

                            {!media.mime_type.includes("image") && (
                              <div className="fileMessage">
                                <span className="fileName">
                                  {media.file_name}
                                </span>
                                <div className="flexRow alignItemsCenter">
                                  {/* <SvgDoc
                                   */}
                                  <span>File</span>
                                </div>
                                <Button
                                  onClick={() => downloadPdf(media)}
                                  className="downloadButton"
                                >
                                  Download
                                </Button>
                              </div>
                            )}
                          </div>
                        ))}
                      {message.message &&
                        (message.url ? (
                          <p
                            className="message"
                            dangerouslySetInnerHTML={{
                              __html: sanitizeHtml(getMessageHtml(message), {
                                allowedTags:
                                  sanitizeHtml.defaults.allowedTags.concat([
                                    "br",
                                  ]),
                              }),
                            }}
                          ></p>
                        ) : (
                          <p
                            className="message"
                            dangerouslySetInnerHTML={{
                              __html: sanitizeHtml(
                                linkifyHtml(message.message, options),
                                {
                                  allowedTags:
                                    sanitizeHtml.defaults.allowedTags.concat([
                                      "br",
                                    ]),
                                }
                              ),
                            }}
                          ></p>
                        ))}
                    </div>
                  </div>
                  <div className="msg-time-container">
                    <div
                      className={
                        nextDate === "" || nextDate !== date
                          ? "msg-time-border"
                          : ""
                      }
                    ></div>
                    <span className={"msg-time"}>
                      {nextDate === "" || nextDate !== date
                        ? getTime(message.created_at)
                        : ""}
                    </span>
                  </div>
                </>
              );
            })}
            {chatsLoader && (
              <span className="messages-loader">Loading old messages...</span>
            )}
          </div>
        ) : chatsLoader ? (
          <div className="d-flex justify-content-center three-dot">
            <ThreeDots
              height="150"
              width="150"
              radius="9"
              color="#637df4"
              ariaLabel="three-dots-loading"
              visible={true}
            />
          </div>
        ) : (
          <div className="norecord">
            <NoRecord />
          </div>
        )}
      </div>
    </ChatMessagesWrapper>
  );
};

export default ChatMesages;
